import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

import { FormSection } from './FormSection';
import { useSpaceConflictPreview } from '../../hooks/useProjectCatalogs';
import { scheduleBounds } from '../../lib/schedule';
import type { ProjectFormValues } from '../../schemas/project.schema';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Input } from '@/shared/components/ui/input';
import { messages } from '@/shared/constants/messages';
import {
  formatDateTimeRange,
  fromDateAndTimeInputs,
  toDateInputValue,
  toTimeInputValue,
} from '@/shared/lib/dates';
import type { Id, SpaceOption } from '@/shared/types/domain';

interface SectionScheduleProps {
  values: ProjectFormValues;
  spaces: SpaceOption[];
  errors: Partial<Record<keyof ProjectFormValues, string>>;
  onChangeNow: (patch: Partial<ProjectFormValues>) => void;
  /** Projeto em edição: ele não deve conflitar consigo mesmo. */
  projectId?: Id;
  /**
   * O dia que veio do menu de contexto do Calendário (spec-calendar.md decisão
   * 75): `/calendario/novo?data=2026-11-21`.
   *
   * ⚠️ **Preenche só a METADE de data, e nunca produz um ISO sozinho.** A pessoa
   * escolheu um dia na grade, não um horário — inventar "19:00" para caber no
   * `timestamptz` gravaria no rascunho uma hora que ninguém digitou, e o alerta
   * de conflito passaria a opinar sobre ela. Enquanto a hora não for digitada,
   * `starts_at` continua nulo, como em qualquer rascunho recém-aberto.
   */
  suggestedDate?: string;
}

/**
 * Data e Local — a seção que carrega o alerta de conflito ao vivo (decisão 15).
 *
 * O título vem da constante compartilhada (decisão 18), então ele mudou aqui
 * junto com o documento e a tela de análise (decisão 37) — é a constante
 * funcionando como projetada, não efeito colateral.
 *
 * Data e hora entram em campos nativos separados e são montadas em ISO com o
 * fuso da igreja por `fromDateAndTimeInputs` (regra 9): o navegador do
 * voluntário pode estar em qualquer fuso, e um pastor viajando não pode ver a
 * conferência deslocada três horas.
 */
export function SectionSchedule({
  values,
  spaces,
  errors,
  onChangeNow,
  projectId,
  suggestedDate,
}: SectionScheduleProps) {
  const { data: conflicts } = useSpaceConflictPreview(
    values.space_ids,
    values.starts_at,
    values.ends_at,
    projectId,
  );

  function handleDateTime(field: 'starts_at' | 'ends_at', date: string, time: string) {
    // Só vira ISO quando as duas metades existem. Guardar meia data seria
    // guardar meia-noite sem a pessoa ter dito isso — e as metades ficam no
    // `DateTimeField`, não aqui, pelo motivo explicado lá embaixo.
    onChangeNow({ [field]: date && time ? fromDateAndTimeInputs(date, time) : null });
  }

  // Recalculado a cada render, e não memoizado: o limite depende de "hoje" e
  // de `starts_at`, e uma tela aberta desde ontem tem de passar a recusar a
  // data de ontem sem esperar recarregamento.
  const bounds = scheduleBounds(values.starts_at);

  function toggleSpace(spaceId: Id, checked: boolean) {
    const next = checked
      ? [...values.space_ids, spaceId]
      : values.space_ids.filter((id) => id !== spaceId);
    onChangeNow({ space_ids: next });
  }

  return (
    <FormSection
      id="data-e-local"
      number={3}
      title={messages.projectSections.schedule}
      description="Um projeto pode ocupar mais de um espaço na mesma janela."
    >
      {/* Data e hora são DOIS controles para uma informação só, e por isso o
          par vive num `fieldset` com `legend` em vez de um `FormField`: um
          `<label>` só não pode apontar para dois campos, e apontá-lo para a
          `div` que os embrulha deixaria os dois sem nome acessível — clicar
          no rótulo não focaria nada e o leitor de tela anunciaria "editar
          texto, em branco". */}
      <div className="grid gap-4 sm:grid-cols-2">
        <DateTimeField
          legend="Início"
          value={values.starts_at}
          suggestedDate={suggestedDate}
          error={errors.starts_at}
          minDate={bounds.startMinDate}
          onChange={(date, time) => handleDateTime('starts_at', date, time)}
        />
        <DateTimeField
          legend="Término"
          value={values.ends_at}
          error={errors.ends_at}
          minDate={bounds.endMinDate}
          minTimeOnDate={bounds.endMinTimeOnDate}
          onChange={(date, time) => handleDateTime('ends_at', date, time)}
        />
      </div>

      <fieldset>
        <legend className="mb-2 text-sm font-medium leading-none">
          Espaços
          <span aria-hidden="true" className="ml-0.5 text-destructive">
            *
          </span>
          <span className="sr-only"> (obrigatório)</span>
        </legend>
        <p className="mb-3 text-xs text-muted-foreground">
          Marque todos os espaços que o projeto vai ocupar. A lista é do cadastro da igreja —
          não há campo livre, para que o alerta de conflito e o calendário continuem confiáveis.
        </p>

        <div className="grid gap-2 sm:grid-cols-2">
          {spaces.map((space) => {
            const checked = values.space_ids.includes(space.id);
            return (
              <label
                key={space.id}
                className="flex cursor-pointer items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm transition-colors hover:bg-accent"
              >
                <Checkbox
                  checked={checked}
                  onChange={(event) => toggleSpace(space.id, event.target.checked)}
                />
                <span className="min-w-0 flex-1 truncate">{space.name}</span>
                {space.capacity ? (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {space.capacity} lugares
                  </span>
                ) : null}
              </label>
            );
          })}
        </div>

        {errors.space_ids ? (
          <p className="mt-2 text-xs font-medium text-destructive">{errors.space_ids}</p>
        ) : null}
      </fieldset>

      {/* US-20: alerta **NÃO bloqueante** (regra 5 / ADR-004) — e, desde a
          decisão 70 da spec do calendário, ele **NOMEIA** o projeto e o
          ministério que ocupam o espaço. A discrição anterior ("já ocupado")
          deixou de proteger o que quer que fosse quando a aba Calendário passou
          a mostrar a agenda da filial aos cinco papéis: o nome está na tela ao
          lado, e calá-lo aqui só impedia o líder de avaliar a gravidade. */}
      {conflicts && conflicts.length > 0 ? (
        <div
          role="status"
          className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm"
        >
          <p className="flex items-center gap-2 font-medium text-amber-900">
            <AlertTriangle aria-hidden="true" className="size-4 shrink-0" />
            {messages.form.conflictTitle}
          </p>
          <ul className="mt-2 space-y-1 text-amber-900/90">
            {conflicts.map((conflict, index) => (
              <li key={`${conflict.space_name}-${index}`}>
                <span className="font-medium">{conflict.space_name}</span>
                {/* `label` continua anulável no tipo, e o fallback fica: o
                    projeto pode não ter título (ADR-005), e "já ocupado" é uma
                    frase melhor do que um travessão solto. */}
                {` · ${conflict.label ?? 'já ocupado'}`}
                {conflict.ministry_name ? ` (${conflict.ministry_name})` : null}
                {conflict.starts_at && conflict.ends_at
                  ? ` · ${formatDateTimeRange(conflict.starts_at, conflict.ends_at)}`
                  : null}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-amber-900/80">
            {messages.form.conflictHint}
          </p>
        </div>
      ) : null}
    </FormSection>
  );
}

interface DateTimeFieldProps {
  legend: string;
  value: string | null;
  /** Metade de data pré-preenchida, sem hora. Ver `suggestedDate` acima. */
  suggestedDate?: string;
  error?: string;
  /** `yyyy-MM-dd` do primeiro dia escolhível — apaga o resto do calendário. */
  minDate?: string;
  /** Piso de horário que só vale se o campo estiver marcado NAQUELE dia. */
  minTimeOnDate?: { date: string; time: string } | null;
  onChange: (date: string, time: string) => void;
}

/**
 * Data + hora — duas metades de uma informação só.
 *
 * **As metades vivem AQUI, em estado local, e não no formulário**, e é isso
 * que faz o campo ser preenchível. O formulário só conhece o ISO combinado; se
 * ele fosse a única memória, digitar a data (ainda sem hora) não produziria
 * valor nenhum, a metade digitada seria descartada, e ao digitar a hora não
 * haveria data com que combinar — os dois campos se apagariam um ao outro para
 * sempre. Descoberto rodando o app: pelo teste unitário e pelo typecheck,
 * estava tudo certo.
 *
 * O `useEffect` de sincronia cobre o caminho contrário: quando o rascunho é
 * carregado do servidor, o ISO chega pronto e as metades precisam refletir
 * isso.
 */
function DateTimeField({
  legend,
  value,
  suggestedDate,
  error,
  minDate,
  minTimeOnDate,
  onChange,
}: DateTimeFieldProps) {
  const [date, setDate] = useState(value ? toDateInputValue(value) : (suggestedDate ?? ''));
  const [time, setTime] = useState(value ? toTimeInputValue(value) : '');

  useEffect(() => {
    if (!value) return;
    setDate(toDateInputValue(value));
    setTime(toTimeInputValue(value));
  }, [value]);

  function update(nextDate: string, nextTime: string) {
    setDate(nextDate);
    setTime(nextTime);
    onChange(nextDate, nextTime);
  }

  const label = legend.toLowerCase();
  const minTime = minTimeOnDate && date === minTimeOnDate.date ? minTimeOnDate.time : undefined;

  return (
    <fieldset className="space-y-1.5">
      <legend className="text-sm font-medium leading-none">
        {legend}
        <span aria-hidden="true" className="ml-0.5 text-destructive">
          *
        </span>
        <span className="sr-only"> (obrigatório)</span>
      </legend>

      <div className="flex gap-2">
        <Input
          type="date"
          aria-label={`Data de ${label}`}
          aria-invalid={error ? true : undefined}
          min={minDate}
          value={date}
          onChange={(event) => update(event.target.value, time)}
        />
        <Input
          type="time"
          className="w-28"
          aria-label={`Hora de ${label}`}
          aria-invalid={error ? true : undefined}
          min={minTime}
          value={time}
          onChange={(event) => update(date, event.target.value)}
        />
      </div>

      {error ? <p className="text-xs font-medium text-destructive">{error}</p> : null}
    </fieldset>
  );
}
